// routes/_layout/components/menu.tsx
import { SignedIn, UserButton } from '@clerk/react-router'
import { Cog6ToothIcon, ShoppingCartIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { href, Link } from 'react-router'
import type { Category, SelectedB2BCustomer } from '~/routes/_layout'
import type { OrgStyling } from '~/services/styling.server'
import { useShopContext } from '~/utils/use-shop-context'
import { B2BCustomerSelector } from './b2b-customer-selector'
import { HamburgerMenu } from './hamburger'

const baseLinks = [
  {
    label: 'Home',
    link: href('/')
  },
  {
    label: 'Winkelwagen',
    link: href('/checkout')
  },
  {
    label: 'Algemene voorwaarden',
    link: href('/terms-conditions')
  },
  {
    label: 'Privacybeleid',
    link: href('/privacy-policy')
  },
  {
    label: 'Verzending en retourneren',
    link: href('/shipping-returns')
  }
]

export default function Menu({
  styling,
  cartCount,
  companyName,
  isAdminArea = false,
  selectedB2BCustomer,
  categories = [],
  categoriesEnabled = false,
  hasArchivedBanner = false
}: {
  companyName: string
  styling: OrgStyling
  cartCount: number
  isAdminArea?: boolean
  selectedB2BCustomer?: SelectedB2BCustomer
  categories?: Category[]
  categoriesEnabled?: boolean
  hasArchivedBanner?: boolean
}) {
  const [scrolled, setScrolled] = useState(false)
  const { admin } = useShopContext()
  const orgRole = admin?.orgRole ?? null
  const isAdmin = admin?.isAdmin ?? false

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const canSeeB2B = isAdmin || orgRole === 'webshop_owner'
  const canSeeWebshopAdmin = isAdmin || orgRole === 'webshop_owner'

  // Build category links if enabled (passed separately for foldout menu)
  const categoryLinks = categoriesEnabled ? categories.map(c => ({ label: c.label, link: href('/category/:key', { key: c.key }) })) : []

  const links = [...baseLinks, ...(canSeeWebshopAdmin ? [{ label: 'Webshop admin', link: '/webshop-admin' }] : [])]

  const roleBadge = orgRole === 'admin' ? 'Admin' : orgRole === 'webshop_owner' ? 'Owner' : null

  return (
    <header
      className={classNames(
        'fixed z-50 w-full border-b bg-(--ws-header-glass) text-(--ws-header-text) backdrop-blur-md transition-all duration-300',
        hasArchivedBanner ? 'top-9' : 'top-0',
        scrolled ? 'shadow-card border-(--ws-border)' : 'border-transparent shadow-none'
      )}>
      <div
        className={classNames('mx-auto flex w-full max-w-5xl items-center justify-between px-4 lg:px-2', {
          'h-16': !scrolled,
          'h-14': scrolled
        })}>
        <HamburgerMenu scrolled={scrolled} links={links} categoryLinks={categoryLinks} />
        <Link to="/" className="flex items-center gap-x-2">
          {styling.logoUrl && (
            <img src={styling.logoUrl} alt="" className={classNames('rounded transition-all duration-300', scrolled ? 'w-9' : 'w-11')} />
          )}
          <div className="hidden items-center gap-2 sm:flex">
            <span className="text-sm font-semibold tracking-[0.12em] uppercase">{companyName}</span>
            {roleBadge && (
              <span
                className={classNames(
                  'rounded-full bg-gradient-to-r from-orange-500 to-red-500 font-semibold text-white shadow-sm transition-all duration-300',
                  scrolled ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'
                )}>
                {roleBadge}
              </span>
            )}
          </div>
        </Link>
        <div className="flex items-center gap-x-4">
          {/* B2B Customer Selector - only visible to webshop owners and admins */}
          {canSeeB2B && !isAdminArea && (
            <div className="w-64">
              <B2BCustomerSelector selectedCustomer={selectedB2BCustomer ?? null} />
            </div>
          )}

          <SignedIn>
            {canSeeWebshopAdmin && !isAdminArea && (
              <Link to="/webshop-admin" className={classNames('transition-all duration-300', scrolled ? 'h-6 w-6' : 'h-7 w-7')} title="Admin Panel">
                <Cog6ToothIcon className="h-full w-full" />
              </Link>
            )}
            <div className="flex w-8 flex-shrink-0 items-center">
              <UserButton afterSignOutUrl="/auth/sign-out-callback" />
            </div>
          </SignedIn>

          {!isAdminArea && (
            <Link to={href('/checkout')} className="relative flex">
              <ShoppingCartIcon className={classNames('transition-all duration-300', scrolled ? 'h-7 w-7' : 'h-8 w-8')} />
              {cartCount > 0 && (
                <div
                  key={cartCount}
                  className={classNames(
                    'absolute top-[-8px] right-[-8px] flex items-center justify-center rounded-full bg-(--ws-accent) font-semibold text-white tabular-nums shadow-sm transition-all duration-200',
                    'motion-safe:animate-[ws-badge-pop_300ms_var(--ease-soft)]',
                    scrolled ? 'h-5 w-5 text-[11px]' : 'h-6 w-6 text-[12px]'
                  )}
                  aria-label={`Winkelwagen: ${cartCount} item${cartCount === 1 ? '' : 's'}`}>
                  {cartCount}
                </div>
              )}
            </Link>
          )}
        </div>
      </div>
    </header>
  )
}
